/**
 * Hook to check if the current user has an active (non-expired) subscription.
 * Used to gate practice actions (record, compare, play) for non-paid users.
 * Reads loggedInUser from localStorage (userId, uid, email); if empty, falls back
 * to Firebase Auth current user so subscriptions are found even when localStorage
 * wasn't set (e.g. WebView, new tab). Queries by userId and email (normalized to
 * lowercase) so docs are always found regardless of case.
 */
import { useState, useEffect } from 'react';
import { collection, query, where, getDocs, limit } from 'firebase/firestore';
import { getFirestore } from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { firebaseApp } from './firebaseConfig';

const db = getFirestore(firebaseApp);
const auth = getAuth(firebaseApp);

const SUB_LOG = '[Subscriptions]';

interface SubscriptionDoc {
  id: string;
  productId: string;
  userId: string | null;
  expiresAt: Date | string | { toDate: () => Date } | { seconds: number };
}

function parseExpiresAt(expiresAt: SubscriptionDoc['expiresAt']): Date {
  if (expiresAt instanceof Date) return expiresAt;
  if (typeof expiresAt === 'string') return new Date(expiresAt);
  if (expiresAt && typeof (expiresAt as { toDate?: () => Date }).toDate === 'function') {
    return (expiresAt as { toDate: () => Date }).toDate();
  }
  if (expiresAt && typeof expiresAt === 'object' && 'seconds' in (expiresAt as object)) {
    return new Date((expiresAt as unknown as { seconds: number }).seconds * 1000);
  }
  return new Date(0);
}

/** Same as PracticePage/PaymentsPage: prefer userId, then uid, then email. Email normalized to lowercase for Firestore match. */
function getLoggedInUserIdAndEmail(stored: string | null): {
  userId: string | null;
  email: string | null;
} {
  if (!stored) return { userId: null, email: null };
  try {
    const parsed = JSON.parse(stored) as { userId?: string; uid?: string; email?: string };
    const userId = parsed?.userId ?? parsed?.uid ?? parsed?.email ?? null;
    const emailRaw = parsed?.email ?? null;
    const email =
      emailRaw && String(emailRaw).trim() !== '' ? String(emailRaw).trim().toLowerCase() : null;
    return {
      userId: userId && String(userId).trim() !== '' ? String(userId).trim() : null,
      email,
    };
  } catch {
    return { userId: null, email: null };
  }
}

export function useHasActiveSubscription(): {
  hasActiveSubscription: boolean;
  loading: boolean;
} {
  const [loading, setLoading] = useState(true);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);

  useEffect(() => {
    const applyIdentity = (u: string | null, e: string | null, source: string) => {
      console.log(SUB_LOG, 'useHasActiveSubscription identity', {
        source,
        hasUserId: !!u,
        userIdLength: u?.length ?? 0,
        hasEmail: !!e,
        emailPrefix: e ? `${e.slice(0, 3)}***` : null,
      });
      setUserId(u);
      setEmail(e);
    };
    const stored = localStorage.getItem('loggedInUser');
    const fromStorage = getLoggedInUserIdAndEmail(stored);
    if (fromStorage.userId || fromStorage.email) {
      applyIdentity(fromStorage.userId, fromStorage.email, 'localStorage');
      return;
    }
    console.log(SUB_LOG, 'useHasActiveSubscription: no localStorage, waiting for Firebase Auth');
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        applyIdentity(
          user.uid,
          user.email?.trim() ? user.email.trim().toLowerCase() : null,
          'Firebase Auth',
        );
      } else {
        applyIdentity(null, null, 'Firebase Auth (signed out)');
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!userId && !email) {
      console.log(
        SUB_LOG,
        'useHasActiveSubscription: no userId/email, setting hasActiveSubscription=false',
      );
      setLoading(false);
      setHasActiveSubscription(false);
      return;
    }

    const load = async () => {
      setLoading(true);
      try {
        const subsRef = collection(db, 'subscriptions');
        const docs: SubscriptionDoc[] = [];
        let countByUserId = 0;
        let countByEmail = 0;

        if (userId) {
          const qUserId = query(subsRef, where('userId', '==', userId), limit(10));
          const snapUserId = await getDocs(qUserId);
          countByUserId = snapUserId.docs.length;
          docs.push(...snapUserId.docs.map((d) => ({ id: d.id, ...d.data() }) as SubscriptionDoc));
          console.log(SUB_LOG, 'useHasActiveSubscription Firestore by userId', {
            count: countByUserId,
          });
        }
        if (email) {
          const emailLower = email.toLowerCase();
          const qEmail = query(subsRef, where('email', '==', emailLower), limit(10));
          const snapEmail = await getDocs(qEmail);
          countByEmail = snapEmail.docs.length;
          const byEmail = snapEmail.docs.map((d) => ({ id: d.id, ...d.data() }) as SubscriptionDoc);
          for (const doc of byEmail) {
            if (!docs.some((d) => d.id === doc.id)) docs.push(doc);
          }
          console.log(SUB_LOG, 'useHasActiveSubscription Firestore by email', {
            count: countByEmail,
            mergedTotal: docs.length,
          });
        }

        const sorted = docs.sort((a, b) => {
          const aTime = parseExpiresAt(a.expiresAt).getTime();
          const bTime = parseExpiresAt(b.expiresAt).getTime();
          return bTime - aTime;
        });
        const doc = sorted[0];
        if (doc) {
          const expiresAt = parseExpiresAt(doc.expiresAt);
          const active = expiresAt > new Date();
          console.log(SUB_LOG, 'useHasActiveSubscription result', {
            docId: doc.id,
            productId: doc.productId,
            expiresAt: expiresAt.toISOString(),
            active,
          });
          setHasActiveSubscription(active);
        } else {
          console.log(SUB_LOG, 'useHasActiveSubscription result: no subscription doc found');
          setHasActiveSubscription(false);
        }
      } catch (err) {
        console.warn(SUB_LOG, 'useHasActiveSubscription error', err);
        setHasActiveSubscription(false);
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, [userId, email]);

  return { hasActiveSubscription, loading };
}
